import {Timer} from "js-vextensions";
import React, {useRef, useCallback, useEffect, useMemo} from "react";
import {Button, Column, Row} from "react-vcomponents";
import {BaseComponent} from "react-vextensions";
import {DefaultSkin, RunInAction_Set, UseWindowEventListener, useForceUpdate} from "web-vcore";
import {store} from "../../../../Store/index.js";
import {SetLockOverlayActive} from "../JSessionUI.js";
import {OverlayPanel} from "../../../../Store/main/tools/journey.js";
import {observer_mgl} from "mobx-graphlink";
import {GetLiveLocalSession_Reactive, GetLiveSession_Reactive} from "../../../../Engine/LiveSessionManagement.js";
import {AlarmsComp} from "../../../../Engine/LiveSession/Components/AlarmsComp.js";
import {GetAlarmsPhaseInfo} from "../JBanner.js";
import {Phase} from "../../../../Server/DB/@Shared/Phase.js";

export const LockOverlay = observer_mgl(()=>{
	const uiState = store.main.engine.journey;
	const session = GetLiveLocalSession_Reactive();

	const [dragState, setDragState] = React.useState<{drag_downPosX: number|null, drag_lastPosX: number|null}>({
		drag_downPosX: null,
		drag_lastPosX: null
	});

	const endDragTimerRef = useRef<Timer|null>(null);
	const [dragStillActive, setDragStillActive] = React.useState(false);

	const forceUpdate = useForceUpdate();
	// update once per second (to keep the "time till phase-change" text up-to-date)
	useEffect(()=>{
		const timer = new Timer(1000, ()=>forceUpdate()).Start();
		return ()=>void(timer.Stop());
	}, [forceUpdate]);
	// also update immediately on window-resize
	useEffect(()=>{
		const onWindowResize = ()=>forceUpdate();
		window.addEventListener("resize", onWindowResize);
		return ()=>window.removeEventListener("resize", onWindowResize);
	}, [forceUpdate]);

	// other mount/unmount effects
	useEffect(()=>{
		SetLockOverlayActive(true);

		// Initialize timer
		endDragTimerRef.current = new Timer(2000, ()=>{
			setDragState({drag_downPosX: null, drag_lastPosX: null});
			setDragStillActive(false);
		}, 1);

		return ()=>{
			SetLockOverlayActive(false);
			endDragTimerRef.current?.Stop();
		};
	}, []);

	const dragDelta_AsPercent = useMemo(()=>{
		const {drag_downPosX, drag_lastPosX} = dragState;
		return drag_downPosX != null && drag_lastPosX != null ? ((drag_lastPosX - drag_downPosX) / window.innerWidth) * 100 : null;
	}, [dragState]);

	const onFirstTouchStart = useCallback((touchPosX: number, touchPosY: number)=>{
		const inEdgeZone =
			(touchPosX <= window.innerWidth * 0.12 && store.main.settings.lockOverlay_dragDirection.IsOneOf("leftToRight", "either"))
			|| (touchPosX >= (window.innerWidth * 0.88) && store.main.settings.lockOverlay_dragDirection.IsOneOf("rightToLeft", "either"));
		if (!inEdgeZone) return;
		setDragState({drag_downPosX: touchPosX, drag_lastPosX: null});
		endDragTimerRef.current?.Start();
		setDragStillActive(true);
	}, []);

	const onFirstTouchMove = React.useCallback((touchPosX: number, touchPosY: number)=>{
		if (!dragStillActive) return;
		setDragState(prev=>({...prev, drag_lastPosX: touchPosX}));
	}, [dragStillActive]);

	const onAllTouchesEnded = React.useCallback(()=>{
		if (!dragStillActive) return;
		if (dragDelta_AsPercent != null && Math.abs(dragDelta_AsPercent) >= 80) {
			RunInAction_Set(()=>store.main.engine.journey.overlay = OverlayPanel.none);
		}
		setDragState({drag_downPosX: null, drag_lastPosX: null});
		setDragStillActive(false);
	}, [dragStillActive, dragDelta_AsPercent]);

	const alarmsComp = session?.Comp(AlarmsComp);
	const phaseInfo = GetAlarmsPhaseInfo(session);
	return (
		<div
			style={{
				position: "fixed", left: 0, top: 0, right: 0, bottom: 0, zIndex: 100,
				display: "flex", alignItems: "center", justifyContent: "center",
				background: "rgba(0, 0, 0, 0.5)", backdropFilter: "blur(3px)",
				touchAction: "none",
			}}
			onTouchStart={e=>e.touches.length == 1 && onFirstTouchStart(e.touches[0].clientX, e.touches[0].clientY)}
			onMouseDown={e=>onFirstTouchStart(e.clientX, e.clientY)}
			onTouchMove={e=>onFirstTouchMove(e.touches[0].clientX, e.touches[0].clientY)}
			onMouseMove={e=>onFirstTouchMove(e.clientX, e.clientY)}
			onTouchEnd={e=>e.touches.length == 0 && onAllTouchesEnded()}
			onMouseUp={e=>onAllTouchesEnded()}
		>
			<Row style={{
				width: "100%", height: "100%",
				transform: dragDelta_AsPercent != null && `translateX(${dragDelta_AsPercent}%)`,
				opacity: .5,
			}}>
				<div style={{
					width: "12%", height: "100%",
					display: "flex", alignItems: "center", justifyContent: "center",
					background: "rgba(0, 0, 0, 0.5)", fontSize: "10vw", color: "rgb(0, 110, 200)",
					opacity: store.main.settings.lockOverlay_dragDirection.IsOneOf("leftToRight", "either") ? 1 : 0,
					fontFamily: DefaultSkin.main.MainFont(),
				}}>🞋</div>
				<Column style={{
					flex: 1, height: "100%",
					display: "flex", alignItems: "center", justifyContent: "center",
					fontSize: "4.3vw", fontWeight: "bold", color: "#FFF",
				}}>
					<div>Swipe from edge to edge to unlock.</div>
					{session != null && <>
						<div style={{marginTop: 5, fontSize: "4vw"}}>{[
							`Phase: ${phaseInfo.phase_niceName}`,
							phaseInfo.timeOfNextPhase_waitingForUser && "(waiting for user action)",
							!phaseInfo.timeOfNextPhase_waitingForUser && `(ends in: ${phaseInfo.timeOfNextPhase_waitingForTime_asSecondsOrMinutesStr?.replace(" minutes", "m").replace(" seconds", "s")})`,
						].filter(a=>a).join(" ")}</div>
						<Button mt={5} text={`Snooze for ${uiState.overlay_snoozeDuration} seconds`} enabled={alarmsComp?.PhaseIs(Phase.dreaming, Phase.solving)} onClick={()=>{
							alarmsComp?.NotifySolveProgress(uiState.overlay_snoozeDuration);
						}}/>
					</>}
				</Column>
				<div style={{
					width: "12%", height: "100%",
					display: "flex", alignItems: "center", justifyContent: "center",
					background: "rgba(0, 0, 0, 0.5)", fontSize: "10vw", color: "rgb(0, 110, 200)",
					opacity: store.main.settings.lockOverlay_dragDirection.IsOneOf("rightToLeft", "either") ? 1 : 0,
					fontFamily: DefaultSkin.main.MainFont(),
				}}>🞋</div>
			</Row>
		</div>
	);
});